import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';

export interface BarChartCardProps<D> {
  title: string;
  subtitle?: string;
  data: D[];
  getLabel: (d: D) => string;
  getValue: (d: D) => number;
  emptyMessage?: string;
  valueFormat?: (v: number) => string;
  barColor?: string;
  orientation?: 'horizontal' | 'vertical';
}

export function BarChartCard<D>({
  title,
  subtitle,
  data,
  getLabel,
  getValue,
  emptyMessage = 'No data',
  valueFormat = (v: number) => String(v),
  barColor = 'text-blue-500 dark:text-blue-400',
  orientation = 'horizontal',
}: BarChartCardProps<D>) {
  if (!data.length) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{title}</CardTitle>
          {subtitle && <p className="text-sm text-app-text-secondary dark:text-gray-400">{subtitle}</p>}
        </CardHeader>
        <CardContent>
          <div className="h-64 flex items-center justify-center text-app-text-secondary dark:text-gray-400">{emptyMessage}</div>
        </CardContent>
      </Card>
    );
  }
  const values = data.map(getValue);
  const maxVal = Math.max(...values, 1);
  const width = 400;
  const height = 240;
  const margin = orientation === 'vertical'
    ? { top: 20, right: 20, bottom: 44, left: 36 }
    : { top: 10, right: 60, bottom: 30, left: 120 };
  const chartW = width - margin.left - margin.right;
  const chartH = height - margin.top - margin.bottom;

  if (orientation === 'vertical') {
    const n = data.length;
    const barGap = 4;
    const barWidth = Math.max(8, (chartW / n) - barGap);
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{title}</CardTitle>
          {subtitle && <p className="text-sm text-app-text-secondary dark:text-gray-400">{subtitle}</p>}
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <svg width={width} height={height} className="min-w-0">
              {data.map((d, i) => {
                const v = getValue(d);
                const barH = maxVal ? (v / maxVal) * chartH : 0;
                const x = margin.left + i * (chartW / n) + (chartW / n - barWidth) / 2;
                const y = margin.top + chartH - barH;
                return (
                  <g key={i}>
                    <rect
                      x={x}
                      y={y}
                      width={barWidth}
                      height={barH}
                      rx={4}
                      className={`fill-current ${barColor}`}
                    />
                    <text x={x + barWidth / 2} y={margin.top + chartH + 14} textAnchor="middle" className="text-xs fill-app-text-secondary dark:fill-gray-400">
                      {getLabel(d)}
                    </text>
                    {barH > 16 && (
                      <text x={x + barWidth / 2} y={y + 12} textAnchor="middle" className="text-xs fill-app-text-secondary dark:fill-gray-400">
                        {valueFormat(v)}
                      </text>
                    )}
                  </g>
                );
              })}
            </svg>
          </div>
        </CardContent>
      </Card>
    );
  }

  const barHeight = Math.max(12, (chartH / data.length) - 4);
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{title}</CardTitle>
        {subtitle && <p className="text-sm text-app-text-secondary dark:text-gray-400">{subtitle}</p>}
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <svg width={width} height={height} className="min-w-0">
            {data.map((d, i) => {
              const v = getValue(d);
              const barW = maxVal ? (v / maxVal) * chartW : 0;
              const y = margin.top + i * (barHeight + 4) + barHeight / 2;
              return (
                <g key={i}>
                  <text x={margin.left - 8} y={y + 4} textAnchor="end" className="text-xs fill-app-text-secondary dark:fill-gray-400">
                    {getLabel(d)}
                  </text>
                  <rect
                    x={margin.left}
                    y={y - barHeight / 2}
                    width={barW}
                    height={barHeight}
                    rx={4}
                    className={`fill-current ${barColor}`}
                  />
                  <text x={margin.left + barW + 6} y={y + 4} className="text-xs fill-app-text-secondary dark:fill-gray-400">
                    {valueFormat(v)}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>
      </CardContent>
    </Card>
  );
}
